import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import moment from 'moment';

import Utils from '../../Utils';

import { IUseRepresentativesForm } from './IUseRepresentativesForm';
import { Representative, RepresentativeErrors, InvestorDTO } from '../../Models';

import { emptyRepresentative, emptyRepresentativeErrors } from '../../Assets/Data/EmptyValues';

const useRepresentativesForm = ({
	translations,
	representativesAPI,
	investorAPI,
	contractAPI,
	placesService,
	unlockNextStep,
	getPlacePredictions,
	personType
}: IUseRepresentativesForm) => {
	const [searchParams] = useSearchParams();

	const [representatives, setRepresentatives] = useState<Representative[]>( personType === 1 ? [] : [{ ...emptyRepresentative }]);
	const [loading, setLoading] = useState<boolean>(false);
	const [contractId, setContractId] = useState<number>(0);
	const [nationalitiesList, setNationalitiesList] = useState<any[]>([]);
	const [errors, setErrors] = useState<RepresentativeErrors[]>([{ ...emptyRepresentativeErrors }]);
	const [noRepresentativesError, setNoRepresentativesError] = useState<boolean>(false);

	const [taxAddressInputs, setTaxAddressInputs] = useState<string[]>([]);
	const [manualAddressVisibility, setManualAddressVisibility] = useState<Record<number, { taxResidence: boolean }>>({});

	const [activeAddress, setActiveAddress] = useState<{ index: number, field: string } | null>(null);

	const updateManualAddressVisibility = (
		index: number,
		field: 'taxResidence',
		visible: boolean,
	) => {
		void field;
		setManualAddressVisibility((prevState) => ({
			...prevState,
			[index]: {
				taxResidence: visible,
			},
		}));
	};

	const normalizeAllowedDocumentType = (documentType?: string) => {
		const normalizedValue = (documentType || '').trim().toLowerCase();
		if (normalizedValue === 'dni') return 'dni';
		if (normalizedValue === 'nif') return 'nif';
		if (normalizedValue === 'passport' || normalizedValue === 'pasaporte') return 'passport';
		return '';
	};

	const getNationalities = () => {
		investorAPI.getCountries().then((res: any) => {
			if (res.length > 0) {
				setNationalitiesList(res);
			}
		});
	};

	const getInvestors = async (contractId: number) => {
		if(personType === 1) return; // No cargar datos si es persona física
		await investorAPI.getInvestors(contractId).then((res: any) => {
			if (res.success) {
				if (res.data.length > 0) {
					const newRepresentatives = res.data.map((x: InvestorDTO, index: number) => {
						let docType = '';
						if (x.nif) docType = 'nif';
						if (x.dni) docType = 'dni';
						if (x.passport) docType = 'passport';
						if (x.mainResidence) {
							setTaxAddressInputs((prevState: string[]) => {
								const updatedInputs = [...prevState];
								updatedInputs[index] = `${x.mainResidence}, ${res.data[0].mainResidencePostalCode}, `
									+ `${res.data[0].mainResidenceCity}, ${res.data[0].mainResidenceCountry}`;
								return updatedInputs;
							});
						}
						return {
							...emptyRepresentative,
							documentType: docType,
							documentNumber: x.dni || x.passport || x.residenceCard || x.nif,
							documentExpiration: x.docValidThru,
							name: x.name,
							lastName: x.lastName,
							secondLastName: x.secondLastName,
							nationality: x.nationality,
							email: x.emailContact,
							phonePrefix: x.phonePrefix,
							phoneNumber: x.phoneNumber,
							birthDate: x.birthDate,
							idContract: x.idContract,
							isSignatory: true,
							taxResidence: x.mainResidence,
							taxResidenceCity: x.mainResidenceCity,
							taxResidencePostalCode: x.mainResidencePostalCode,
							countryTaxResidence: x.mainResidenceCountry,
							taxResidenceExtra: x.mainResidenceExtra,
						};
					});
					setRepresentatives(newRepresentatives);
					setErrors(newRepresentatives.map(() => ({ ...emptyRepresentativeErrors })));
				}
			}
		});
	};

	const configureInitialData = async (ContractId: number) => {
		setLoading(true);
		setContractId(ContractId);
		await representativesAPI.getLegalRepresentatives(ContractId).then(async (res: any) => {
			if (res.success) {
				if (res.data.length > 0) {
					setErrors(res.data.map(() => ({ ...emptyRepresentativeErrors })));
					res.data.map((representative: Representative, index: number) => {
						if (representative.taxResidence) {
							setTaxAddressInputs((prevState: string[]) => {
								const updatedInputs = [...prevState];
								updatedInputs[index] = `${representative.taxResidence}, `
									+ `${res.data[0].taxResidencePostalCode}, `
									+ `${res.data[0].taxResidenceCity}, `
									+ `${res.data[0].countryTaxResidence}`;
								return updatedInputs;
							});
						}

						return representative;
					});
					setRepresentatives(res.data.map((representative: Representative) => ({
						...representative,
						documentType: normalizeAllowedDocumentType(representative.documentType),
					})));
				} else {
					await getInvestors(ContractId);
				}
			}
		});
		setLoading(false);
	};

	useEffect(() => {
		if (searchParams.has('ContractId')) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = (hash: string) => (
				contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					}
				})
			);

			getContractIdByHash(hash!);
		}
		getNationalities();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams.has('ContractId')]);

	const addRow = () => {
		const newElement: Representative = { ...emptyRepresentative };
		setRepresentatives((prevState: Representative[]) => [...prevState, newElement]);
		setErrors((prevState: RepresentativeErrors[]) => [...prevState, { ...emptyRepresentativeErrors }]);
	};

	const handleChangeData = (index: number) => (e: any) => {
		const { name, value } = e.target;
		setErrors((prevState: RepresentativeErrors[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[name]: false,
				invalidNameFormat: name === 'name' ? false : updatedErrors[index || 0].invalidNameFormat,
				invalidLastNameFormat: name === 'lastName' ? false : updatedErrors[index || 0].invalidLastNameFormat,
				notValidDocument: name === 'nif' ? false : updatedErrors[index || 0].notValidDocument,
				notValidEmail: name === 'email' ? false : updatedErrors[index || 0].notValidEmail,
				notValidExpirationDate: name === 'documentExpiration'
					? false
					: updatedErrors[index || 0].notValidExpirationDate,
			};
			return updatedErrors;
		});
		setRepresentatives((prevState: Representative[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: value,
			};
			return updatedInvestors;
		});
	};

	const handleChangeDate = (field: string, index: number) => (date: any) => {
		setRepresentatives((prevState: Representative[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined,
			};
			return updatedInvestors;
		});
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
	};

	const handleChangeSwitch = (field: string, index: number) => (date: any) => {
		setRepresentatives((prevState: Representative[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined,
			};
			return updatedInvestors;
		});
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
	};

	const handleCheckedData = (index: number) => (e: any) => {
		const { name } = e.target;
		setRepresentatives((prevState: Representative[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: e.target.checked,
			};
			return updatedInvestors;
		});
	};

	const handleSelectData = (field: string, index?: number) => (value: string) => {
		setErrors((prevState: any[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false,
			};
			return updatedErrors;
		});
		setRepresentatives((prevState: Representative[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index || 0] = {
				...updatedInvestors[index || 0],
				[field]: value,
			};
			return updatedInvestors;
		});
	};

	const handleChangePRPData = (e: any) => {
		const { name, value } = e.target;
		const newErrors = [...errors];
		newErrors[0][name as keyof RepresentativeErrors] = false;
		setErrors(newErrors);

		const array = [...representatives as any];
		array[0][name] = value;
		setRepresentatives(array);
	};
	
	const deleteRow = async (i: number) => {
		if (representatives.length > 1 || personType === 1) { // Permitir eliminar si es persona física y queda 0 apoderados
			if (representatives[i].id > 0) {
				await representativesAPI.deleteLegalRepresentative(representatives[i].id)
					.then((res: any) => {
						if (res.success) {
							setRepresentatives((prevState: Representative[]) => [
								...prevState.filter((_: any, index: number) => index !== i),
							]);
							setErrors((prevState: RepresentativeErrors[]) => [
								...prevState.filter((_, index) => index !== i),
							]);
						} else {
							message.error(translations.messageError);
						}
					});
			} else {
				setRepresentatives((prevState: Representative[]) => [...prevState.filter((_, index) => index !== i)]);
				setErrors((prevState: RepresentativeErrors[]) => [...prevState.filter((_, index) => index !== i)]);
			}
		} else {
			message.error(translations.apoderadoMinimo);
		}
	};

	const validateRepresentatives = (data: Representative, index: number) => {
		let isOK = true;
		const newErrors = [...errors];
		if (!data.documentType) {
			newErrors[index].documentType = true;
			isOK = false;
		}
		if (!data.documentNumber) {
			newErrors[index].documentNumber = true;
			isOK = false;
		} else if (!/^[a-zA-Z0-9]*$/g.test(data.documentNumber!)) {
			newErrors[index].notValidDocument = true;
			isOK = false;
		}
		if (!data.name) {
			newErrors[index].name = true;
			isOK = false;
		} else if (!Utils.validate('name', data.name)) {
			newErrors[index].invalidNameFormat = true;
			isOK = false;
		}
		if (!data.lastName) {
			newErrors[index].lastName = true;
			isOK = false;
		} else if (!Utils.validate('name', data.lastName)) {
			newErrors[index].invalidLastNameFormat = true;
			isOK = false;
		}
		if (!data.secondLastName && data.nationality === 'Portugal') {
			newErrors[index].secondLastName = true;
			isOK = false;
		}
		if (data.secondLastName && !Utils.validate('name', data.secondLastName)) {
			newErrors[index].invalidSecondLastNameFormat = true;
			isOK = false;
		}
		if (!data.nationality) {
			newErrors[index].nationality = true;
			isOK = false;
		}
		if (!data.email || !Utils.validate('email', data.email)) {
			newErrors[index].email = true;
			isOK = false;
		}
		if (!data.documentExpiration) {
			newErrors[index].documentExpiration = true;
			isOK = false;
		} else if (moment(data.documentExpiration).isBefore(moment())) {
			newErrors[index].notValidExpirationDate = true;
			isOK = false;
		}
		if (!data.birthDate) {
			newErrors[index].birthDate = true;
			isOK = false;
		}
		if (!data.administrator) {
			newErrors[index].administrator = true;
			isOK = false;
		}
		if (!data.administratorType) {
			newErrors[index].administratorType = true;
			isOK = false;
		}
		if (data.prP5) {
			if (!data.namePRP51) {
				newErrors[index].namePRP51 = true;
				isOK = false;
			}
			if (!data.positionPRP51) {
				newErrors[index].positionPRP51 = true;
				isOK = false;
			}
		}
		if (!data.taxResidence || !data.taxResidenceCity || !data.taxResidencePostalCode || !data.countryTaxResidence) {
			newErrors[index].taxResidence = true;
			newErrors[index].taxResidenceCity = !data.taxResidenceCity;
			newErrors[index].taxResidencePostalCode = !data.taxResidencePostalCode;
			newErrors[index].countryTaxResidence = !data.countryTaxResidence;
			updateManualAddressVisibility(index, 'taxResidence', true);
			isOK = false;
		} else {
			updateManualAddressVisibility(index, 'taxResidence', false);
		}
		setErrors(newErrors);
		return isOK;
	};

	const postRepresentatives = async (newRepresentatives: Representative[]) => {
		await representativesAPI.saveLegalRepresentatives(newRepresentatives)
			.then((res: any) => {
				if (res.success) {
					message.success(translations.datosGuardados);
					unlockNextStep(contractId);
				} else {
					message.error(translations.messageError);
				}
			});
	};

	const updateRepresentatives = async (updatedRepresentatives: Representative[]) => {
		await representativesAPI.updateLegalRepresentatives(updatedRepresentatives)
			.then((res: any) => {
				if (res.success) {
					message.success(translations.datosGuardados);
				} else {
					message.error(translations.messageError);
				}
			});
	};

	const saveData = async () => {
		if (representatives.length > 0) {
			setLoading(true);
			let isDataOK = true;
			representatives.map((representative: Representative, i) => {
				const isDataValid = validateRepresentatives(representative, i);
				if (!isDataValid) {
					isDataOK = false;
				} else {
					const updatedRepresentative = {
						...representative,
						birthDate: moment(representative.birthDate).format('YYYY-MM-DD'),
						documentExpiration: moment(representative.documentExpiration).format('YYYY-MM-DD'),
						idContract: contractId,
					};
					representatives[i] = updatedRepresentative;
				}

				return representatives[i];
			});
			const newRepresentatives = representatives.filter((x: any) => !x.id);
			const updatedRepresentatives = representatives.filter((x: any) => x.id > 0);

			if (isDataOK) {
				if (newRepresentatives.length > 0) {
					await postRepresentatives(newRepresentatives);
				}
				if (updatedRepresentatives.length > 0) {
					await updateRepresentatives(updatedRepresentatives);
				}
			}
		} else {
			setNoRepresentativesError(true);
		}
		setLoading(false);
	};

	const getAddressComponent = (placeDetails: any, type: string) => {
		const component = placeDetails?.address_components?.find((component: any) => component.types.includes(type));
		return component ? component.long_name : '';
	};

	const getFormattedStreetAddress = (placeDetails: any, fallbackValue: string) => {
		const route = getAddressComponent(placeDetails, 'route');
		const streetNumber = getAddressComponent(placeDetails, 'street_number');
		const premise = getAddressComponent(placeDetails, 'premise');
		const subpremise = getAddressComponent(placeDetails, 'subpremise');
		const pointOfInterest = getAddressComponent(placeDetails, 'point_of_interest');
		const neighborhood = getAddressComponent(placeDetails, 'neighborhood');
		const placeName = placeDetails?.name?.trim?.() || '';
		const fallbackStreet = fallbackValue
			.split(',')[0]
			.split(' - ')[0]
			.trim();

		return [route, streetNumber].filter(Boolean).join(', ')
			|| premise
			|| subpremise
			|| placeName
			|| pointOfInterest
			|| neighborhood
			|| fallbackStreet;
	};

	const handleSelectAddress = (index: number, field: string) => (value: string, option: any) => {
		if (!activeAddress) return;
		placesService?.getDetails(
			{ placeId: option.key },
			(placeDetails: any) => {
				const address = getFormattedStreetAddress(placeDetails, value);
				const city = getAddressComponent(placeDetails, 'locality')
					|| getAddressComponent(placeDetails, 'administrative_area_level_2');
				const postalCode = getAddressComponent(placeDetails, 'postal_code');
				const country = getAddressComponent(placeDetails, 'country');

				setRepresentatives((prevState: Representative[]) => {
					const updatedInvestors = [...prevState];
					const updatedInvestor = { ...updatedInvestors[index] };

					if (field === 'taxResidence') {
						setTaxAddressInputs((prevState: string[]) => {
							const updatedInputs = [...prevState];
							updatedInputs[index] = value;
							return updatedInputs;
						});
						updatedInvestor.taxResidence = address;
						updatedInvestor.taxResidenceCity = city;
						updatedInvestor.taxResidencePostalCode = postalCode;
						updatedInvestor.countryTaxResidence = country;
					}

					updatedInvestors[index] = updatedInvestor;
					return updatedInvestors;
				});
				setErrors((prevState: RepresentativeErrors[]) => {
					const updatedErrors = [...prevState];
					updatedErrors[index] = {
						...updatedErrors[index],
						taxResidence: !address || !city || !postalCode || !country,
						taxResidenceCity: !city,
						taxResidencePostalCode: !postalCode,
						countryTaxResidence: !country,
					};
					return updatedErrors;
				});
				updateManualAddressVisibility(index, 'taxResidence', !address || !city || !postalCode || !country);
			},
		);
	};

	const handleSearchAddress = (index: number, field: string) => (value: string) => {
		setActiveAddress({ index, field });
		getPlacePredictions({ input: value });
	};

	return {
		loading,
		noRepresentativesError,
		errors,
		nationalitiesList,
		contractId,
		representatives,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		addRow,
		deleteRow,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		handleChangeSwitch,
		handleCheckedData,
		handleChangePRPData,
		saveData,
		handleSelectAddress,
		handleSearchAddress,
	};
};

export default useRepresentativesForm;
